import { node, string } from "prop-types";

import styled from "styled-components/macro";

import FnrTypography from "UI/FnrTypography";

/**
 * Title row that seats the USPS-Verified badge immediately after the field title (the same
 * placement the admin profile uses). The badge is always mounted and fades in/out on `verified`
 * via its own FnrOpacityToggle, so surfaces render this row unconditionally rather than swapping to
 * a plain label — the title clips with an ellipsis to give the badge room (see TitleText), even
 * while the badge is faded out (its box still reserves width by design).
 *
 * Owns only the row geometry; text cosmetics (`color`, `type`, …) forward to the title's
 * FnrTypography. `color` defaults to "inherit" so a `styled(BadgeTitleRow)` twin can set it via CSS.
 */
export default function BadgeTitleRow({
  badge,
  className,
  children,
  color = "inherit",
  ...typographyProps
}) {
  return (
    <Row className={className}>
      <TitleText color={color} {...typographyProps}>
        {children}
      </TitleText>
      <BadgeSlot>{badge}</BadgeSlot>
    </Row>
  );
}

BadgeTitleRow.propTypes = {
  badge: node,
  className: string,
  children: node,
  color: string,
};

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// flex: 0 1 auto — a short title hugs its text so the badge sits right beside
// it; a long title shrinks (never the badge) and clips with an ellipsis.
const TitleText = styled(FnrTypography)`
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BadgeSlot = styled.span`
  flex-shrink: 0;
  display: inline-flex;
`;
